import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../config/api';

// Default TuPlazaIA branding
const DEFAULT_BRANDING = {
  name: 'TuPlazaIA',
  logo: null, // Will use SVG component
  logoUrl: null,
  favicon: '/favicon.svg',
  primaryColor: '#6366F1', // Indigo
  secondaryColor: '#8B5CF6', // Purple
  accentColor: '#10B981', // Emerald
  backgroundColor: '#F8FAFC',
  textColor: '#1E293B',
  tagline: 'Prepara tu oposición con IA',
  supportEmail: 'soporte@tuplazaia.com',
  supportPhone: null,
  socialLinks: {
    instagram: null,
    twitter: null,
    linkedin: null,
    youtube: null
  },
  features: {
    showTuPlazaIABranding: true, // "Powered by TuPlazaIA"
    customDomain: false,
    customEmails: false,
    whiteLabel: false
  },
  appStoreUrl: null,
  playStoreUrl: null,
  pwaEnabled: true
};

const BrandingContext = createContext(null);

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [institution, setInstitution] = useState(null);
  const [isWhiteLabel, setIsWhiteLabel] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    detectAndLoadBranding();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only: branding detection should run once based on current URL/domain
  }, []);


  const detectAndLoadBranding = async () => {
    try {
      // Check if we're on a custom domain or subdomain
      const hostname = window.location.hostname;
      const isCustomDomain = !hostname.includes('tuplazaia.com') &&
                            !hostname.includes('localhost');

      // Check for subdomain pattern: academy.tuplazaia.com
      const subdomainMatch = hostname.match(/^([a-z0-9-]+)\.(tuplazaia\.com)$/i);
      const subdomain = subdomainMatch ? subdomainMatch[1] : null;
      
      // Check localStorage for institution context
      const storedInstitution = localStorage.getItem('institution_context');
      
      if (isCustomDomain || subdomain || storedInstitution) {
        // Load institution branding
        let institutionId = storedInstitution ? JSON.parse(storedInstitution).institution_id : null;
        
        if (!institutionId && (isCustomDomain || subdomain)) {
          // Resolve domain/subdomain to institution
          try {
            const response = await axios.get(`${API}/branding/resolve`, {
              params: { 
                domain: isCustomDomain ? hostname : null,
                subdomain: subdomain 
              }
            });
            institutionId = response.data.institution_id;
          } catch (_e) {
            // No institution found for domain
          }
        }
        
        if (institutionId) {
          await loadInstitutionBranding(institutionId);
          return;
        }
      }
      
      // Default TuPlazaIA branding
      applyBrandingStyles(DEFAULT_BRANDING);
    } catch (_error) {
      // Branding loading failed, using defaults
    } finally {
      setLoading(false);
    }
  };

  const loadInstitutionBranding = async (institutionId) => {
    try {
      const response = await axios.get(`${API}/branding/${institutionId}`);
      const institutionBranding = response.data;
      
      setInstitution(institutionBranding.institution);
      setIsWhiteLabel(institutionBranding.whiteLabel || false);
      
      const mergedBranding = {
        ...DEFAULT_BRANDING,
        ...institutionBranding.branding,
        features: {
          ...DEFAULT_BRANDING.features,
          ...institutionBranding.features
        }
      };
      
      setBranding(mergedBranding);
      applyBrandingStyles(mergedBranding);
      
      // Store for persistence
      localStorage.setItem('institution_context', JSON.stringify({
        institution_id: institutionId,
        name: institutionBranding.institution?.name
      }));
    } catch (_error) {
      applyBrandingStyles(DEFAULT_BRANDING);
    }
  };

  const applyBrandingStyles = (brandingConfig) => {
    // Apply CSS variables for theming
    const root = document.documentElement;
    root.style.setProperty('--color-primary', brandingConfig.primaryColor);
    root.style.setProperty('--color-secondary', brandingConfig.secondaryColor);
    root.style.setProperty('--color-accent', brandingConfig.accentColor);
    root.style.setProperty('--color-background', brandingConfig.backgroundColor);
    root.style.setProperty('--color-text', brandingConfig.textColor);
    
    // Update page title
    document.title = `${brandingConfig.name} - ${brandingConfig.tagline}`;
    
    // Update theme color
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta) {
      themeColorMeta.setAttribute('content', brandingConfig.primaryColor);
    }
    
    // Update favicon if custom
    if (brandingConfig.favicon && brandingConfig.favicon !== '/favicon.svg') {
      const faviconLink = document.querySelector('link[rel="icon"]');
      if (faviconLink) {
        faviconLink.href = brandingConfig.favicon;
      }
    }
  };

  const clearInstitutionContext = () => {
    localStorage.removeItem('institution_context');
    setInstitution(null);
    setIsWhiteLabel(false);
    setBranding(DEFAULT_BRANDING);
    applyBrandingStyles(DEFAULT_BRANDING);
  };

  const setInstitutionContext = async (institutionId) => {
    await loadInstitutionBranding(institutionId);
  };

  const value = {
    branding,
    institution,
    isWhiteLabel,
    loading,
    showPoweredBy: !isWhiteLabel || branding.features?.showTuPlazaIABranding,
    clearInstitutionContext,
    setInstitutionContext,
    // Helper methods
    getPrimaryColor: () => branding.primaryColor,
    getSecondaryColor: () => branding.secondaryColor,
    getAccentColor: () => branding.accentColor,
    getLogoUrl: () => branding.logoUrl,
    getAppName: () => branding.name,
    getSupportEmail: () => branding.supportEmail
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
};

export default BrandingContext;
